// dropCheckConstraint — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-check-constraint', constraintName })` op
// (DSE2 §3.B4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B4.
//
// Per-engine rules (mirrors DSE2's `synthesizeDropCheckConstraint`):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql / pgsql / oracle: emit `DROP CONSTRAINT name`.
//   - mysql: emits `DROP CHECK name` — mysql's recommended idiom on 8.0.16+
//     (`DROP CONSTRAINT` works on 8.0.19+ but `DROP CHECK` is universally
//     compatible across both releases).
//   - pgsql / oracle: optional `cascade?: boolean` appends `CASCADE`.

import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type DropCheckConstraintArgs = {
  readonly constraintName: string;
  readonly cascade?: boolean;
};

export function buildDropCheckConstraintEdit(args: DropCheckConstraintArgs): DdlEdit {
  return {
    op: 'drop-check-constraint',
    constraintName: args.constraintName,
    cascade: args.cascade,
  };
}

export async function commitDropCheckConstraint(
  ctx: VqeDdlEditContext,
  args: DropCheckConstraintArgs,
): Promise<void> {
  const edit = buildDropCheckConstraintEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isDropCheckConstraintSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getDropCheckConstraintUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … DROP CHECK. Recreate '
      + 'the table without the constraint — use “Edit as SQL” to write '
      + 'the change.'
    );
  }
  return '';
}

/** Cascade is accepted on the DROP CONSTRAINT clause for postgres + oracle.
 *  mysql emits `DROP CHECK name` and silently ignores cascade; mssql does
 *  not accept the keyword in this position. */
export function isCascadeSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

/** Pre-flight validator: every supported engine requires `constraintName`;
 *  cascade is rejected on mssql + mysql. */
export function validateDropCheckConstraintForEngine(
  engine: SqlEngine,
  args: DropCheckConstraintArgs,
): string | null {
  if (engine === 'sqlite') return getDropCheckConstraintUnsupportedTooltip(engine);
  const name = args.constraintName.trim();
  if (name === '') {
    return `${engine} requires the constraint name to drop a CHECK constraint.`;
  }
  if (!isCascadeSupported(engine) && args.cascade === true) {
    return 'CASCADE is supported on PostgreSQL + Oracle only.';
  }
  return null;
}
